import { useEffect } from 'react';
import { Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Text } from '@eo-locale/react';
import { styled, useWidget } from '@guest-widgets/core';
import { booking } from '@guest-widgets/shared/src/classes/booking';
import { ICON_BASE_SIZE, LoadingButton } from '@guest-widgets/shared/src/components/LoadingButton';
import { useQueryRouter } from '@guest-widgets/shared/src/components/Router/QueryRouter';
import { config } from '@guest-widgets/shared/src/config';

import { NavigationContainer, StyledErrorContainer } from '../StyledSharedComponents';
import { ActionStatus, ApiInvalidStructureException } from '../contexts/apiContext/apiContext';
import { ErrorMessage } from '../Error';
import { useWidgetStore } from '../contexts/storesContext/useWidgetStore';
import { WarningDialog } from '../common/WarningDialog';
import { useWarningDialogHandler } from '../hooks/upsell/useWarningDialogHandler';
import { useUpsell } from '../contexts/upsellContext/upsellContext';

const { productIdQueryKey, productTypeQueryKey } = config;

export interface NavigationProps {
  actionStatus: ActionStatus[];
  parentErrors?: string[];
  disabled: boolean;
  onAddToCart?: () => void;
  rateLimitError?: boolean;
}

export const Navigation = ({
  actionStatus,
  disabled,
  parentErrors,
  rateLimitError,
}: NavigationProps) => {
  const [cartStatus, upsellStatus] = actionStatus;
  const { currentItem } = useUpsell();
  const {
    isWarningOpen,
    isWarningNeeded,
    openWarningDialog,
    closeWarningDialog,
    removeParentFromCart,
    trigger,
  } = useWarningDialogHandler();
  const nextStep = useWidgetStore('routerStore', (state) => state.nextStep);
  const previousStep = useWidgetStore('routerStore', (state) => state.previousStep);
  const queryRouter = useQueryRouter();
  const { isMobileSize } = useWidget();

  useEffect(() => {
    const handleUnload = () => removeParentFromCart(currentItem);
    const handleBeforeUnload = (event: BeforeUnloadEvent) =>
      isWarningNeeded && event.preventDefault();
    const handlePopState = () => isWarningNeeded && openWarningDialog('back-history');
    const handleGoBack = (event: Event) => {
      if (isWarningNeeded) {
        event.preventDefault();
        openWarningDialog('back-history');
      }
    };

    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('product-details-go-back', handleGoBack);

    return () => {
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('product-details-go-back', handleGoBack);
    };
  }, [isWarningNeeded]);

  const goBackToCatalog = () => {
    queryRouter.delete(productIdQueryKey);
    queryRouter.delete(productTypeQueryKey);
    window.location.reload();
  };

  const goBackToProduct = () => {
    window.history.back();
  };

  const handlePreviousStep = () => {
    if (isWarningNeeded) {
      return openWarningDialog('backward');
    }
    previousStep();
  };

  const handleConfirm = async () => {
    await removeParentFromCart(currentItem);

    if (trigger === 'backward') return previousStep();
    if (trigger === 'back-history') isMobileSize() ? goBackToProduct() : goBackToCatalog();
  };

  const renderErrors = () => {
    if (rateLimitError)
      return <ErrorMessage show messageKey={'rate-limited-error'} mt={0} mb={0} />;
    if (parentErrors?.length)
      return parentErrors.map((error) => (
        <ErrorMessage key={error} show={!!error} message={error} mt={0} mb={0} />
      ));
    if (cartStatus.error instanceof ApiInvalidStructureException)
      return <ErrorMessage show={cartStatus.isError} message={cartStatus.error.message} mt={0} />;
    return <ErrorMessage show={cartStatus.isError} messageKey="server-unreachable" mt={0} />;
  };

  return (
    <FloatingButtons className={booking.navigation.root}>
      <StyledErrorContainer>{renderErrors()}</StyledErrorContainer>
      <LoadingButton
        loading={cartStatus.isLoading || upsellStatus.isFetching!}
        variant="contained"
        color="primary"
        size="large"
        iconSize={ICON_BASE_SIZE}
        fullWidth
        onClick={nextStep}
        disabled={disabled}
        className={`${booking.buttons.button} ${booking.buttons.upsell}`}
      >
        <Text id="continue-to-checkout" />
      </LoadingButton>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ChevronLeftIcon />}
        onClick={handlePreviousStep}
        fullWidth
        className={booking.buttons.back}
      >
        <Text id="continue-shopping" />
      </Button>
      <WarningDialog
        open={isWarningOpen}
        onClose={closeWarningDialog}
        onConfirm={handleConfirm}
        productName={currentItem?.product.name ?? ''}
        rateLimitError={rateLimitError}
      />
    </FloatingButtons>
  );
};

const MARGIN = 16;
const BACK_BUTTON_HEIGHT = 42;

const FloatingButtons = styled(NavigationContainer)({
  position: 'sticky',
  bottom: (BACK_BUTTON_HEIGHT + MARGIN) * -1,
  left: 0,
  right: 0,
  background: 'linear-gradient(transparent 25%, #FFFFFF 60%)',
  zIndex: 1,
});
